import dayjs from "dayjs";
import { orderState } from "lib/recoil/atoms";
import { useRouter } from "next/router";
import { useCallback } from "react";
import { useRecoilState } from "recoil";
import { repository } from "repositories";
import { days } from "shared/constants";
import { useErrorMessage } from "shared/hooks/useErrorMessage";
import { useFlashContext } from "shared/hooks/useFlashContext";
import { CalledFortuneTeller, FortuneTeller } from "shared/types";
import tw, { css } from "twin.macro";
import { Paths } from "utils/routes";
import { useOrderModal } from "./useOrderModal";
import { AlertInAvailableMenuModal } from "features/order/components/AlertInAvailableMenuModal";
import { useModal } from "mui-modal-provider";

const radialGradientCss = css({
  background: "radial-gradient(circle at top, #FF4CE1, #FF9892)",
});

const getLastCalledAt = (fortuneTeller: CalledFortuneTeller) => {
  return fortuneTeller?.lastCall
    ? dayjs(fortuneTeller.lastCall.callEndedAt)
    : null;
};

const getLastCallDuration = (fortuneTeller: CalledFortuneTeller) => {
  return fortuneTeller?.lastCall
    ? dayjs.unix(fortuneTeller.lastCall.callDuration).add(-9, "hour")
    : null;
};

export const useCallInfo = () => {
  const { toggleFlash } = useFlashContext();
  const { handleAxiosError } = useErrorMessage();
  const { push } = useRouter();
  const [recoilState, setRecoilState] = useRecoilState(orderState);
  const { showModal } = useModal();
  const { showModal: showOrderModal } = useOrderModal();

  const subscribeCallEndNotification = useCallback(
    async (fortuneTeller: CalledFortuneTeller) => {
      try {
        await repository.callEndNotification.subscribeCallEndNotification({
          fortuneTellerId: fortuneTeller.id,
        });
        toggleFlash({ message: "順番待ち通知の設定が完了しました" });
      } catch (error) {
        handleAxiosError(error);
      }
    },
    [toggleFlash, handleAxiosError]
  );

  const goToSelectTelMenu = useCallback(
    ({
      fortuneTeller,
      fromNow,
    }: {
      fortuneTeller: Pick<FortuneTeller, "id">;
      fromNow: boolean;
    }) => {
      setRecoilState({ ...recoilState, _type: "tel", fromNow });
      push(Paths.order.tel.menu(fortuneTeller.id));
    },
    [setRecoilState, recoilState, push]
  );

  const callInfo = {
    getLastCalledAt,
    getLastCallDuration,
    getLastCalledDate: (fortuneTeller: CalledFortuneTeller) => {
      const lastCalledAt = getLastCalledAt(fortuneTeller);
      return lastCalledAt ? lastCalledAt.format("YYYY-MM-DD") : null;
    },
    getLastCalledDay: (fortuneTeller: CalledFortuneTeller) => {
      const lastCalledAt = getLastCalledAt(fortuneTeller);
      return lastCalledAt ? days[lastCalledAt.day()] : null;
    },
    getLastCalledHour: (fortuneTeller: CalledFortuneTeller) => {
      const lastCalledDuration = getLastCallDuration(fortuneTeller);
      return lastCalledDuration && lastCalledDuration.hour()
        ? `${lastCalledDuration.hour()}h `
        : "";
    },
    getLastCalledMinute: (fortuneTeller: CalledFortuneTeller) => {
      const lastCalledDuration = getLastCallDuration(fortuneTeller);
      return lastCalledDuration ? `${lastCalledDuration.minute()}min` : null;
    },
    getStatus: (fortuneTeller: CalledFortuneTeller) => {
      if (!fortuneTeller) {
        return null;
      } else if (fortuneTeller.isCalling) {
        return `${dayjs().format("HH:mm")}〜通話中`;
      } else if (!fortuneTeller.telAcceptFlag) {
        return "受付停止中";
      } else if (fortuneTeller.telWaitingFlag) {
        const waitedAt = fortuneTeller.currentTelWaitingHistory?.startedAt
          ? dayjs(fortuneTeller.currentTelWaitingHistory?.startedAt)
          : null;

        return waitedAt ? `${waitedAt.format("HH:mm")}〜待機中` : null;
      } else if (fortuneTeller.telAcceptFlag) {
        return "電話予約受付中";
      }
    },
    getButtonLabel: (
      fortuneTeller: CalledFortuneTeller,
      options?: { shortHand: boolean }
    ) => {
      const { shortHand = false } = options || {};
      if (!fortuneTeller) {
        return null;
      } else if (fortuneTeller.isCalling) {
        return shortHand ? "順番待ち" : "順番待ちする";
      } else if (!fortuneTeller.telAcceptFlag) {
        return shortHand ? "" : "受付停止中";
      } else if (fortuneTeller.telWaitingFlag) {
        return shortHand ? "発信" : "いますぐ相談";
      } else if (fortuneTeller.telAcceptFlag) {
        return shortHand ? "予約" : "いますぐ予約";
      }
    },
    getButtonStyle: (fortuneTeller: CalledFortuneTeller) => {
      if (!fortuneTeller) {
        return null;
      } else if (fortuneTeller.isCalling) {
        return tw`from-[#FF9892] to-[#FF9892]`;
      } else if (!fortuneTeller.telAcceptFlag) {
        return tw`from-secondary to-secondary cursor-not-allowed`;
      } else if (fortuneTeller.telWaitingFlag) {
        return [
          tw`border-[4px] border-solid border-[#FFCCF2]`,
          radialGradientCss,
        ];
      } else if (fortuneTeller.telAcceptFlag) {
        return tw`from-white to-white border border-solid border-[#FF9892] text-[#FF9892]`;
      }
    },
    getStatusStyle: (fortuneTeller: CalledFortuneTeller) => {
      if (!fortuneTeller) {
        return null;
      } else if (fortuneTeller.isCalling) {
        return tw`text-[#FE4FD0]`;
      } else if (!fortuneTeller.telAcceptFlag) {
        return tw`text-secondary`;
      } else if (fortuneTeller.telWaitingFlag) {
        return tw`text-[#FF9892]`;
      } else if (fortuneTeller.telAcceptFlag) {
        return tw`text-[#FF9892]`;
      }
    },
    onClick: async (fortuneTeller: CalledFortuneTeller) => {
      if (!fortuneTeller) {
        return;
      } else if (fortuneTeller.isBlocked) {
        showModal(AlertInAvailableMenuModal);
      } else if (fortuneTeller.isCalling) {
        await subscribeCallEndNotification(fortuneTeller);
      } else if (!fortuneTeller.telAcceptFlag) {
        return;
      } else if (fortuneTeller.telWaitingFlag) {
        showOrderModal(fortuneTeller);
      } else if (fortuneTeller.telAcceptFlag) {
        goToSelectTelMenu({ fortuneTeller, fromNow: false });
      }
    },
    goToSelectTelMenu,
  };

  return callInfo;
};
